<template>
  <div class="newbie-guide-container">
    <div class="guide-left">
      <div class="guide-title">
        <h2 class="title-main">千寻AI智能招聘</h2>
        <p class="title-subtitle">简述一下你的详细信息</p>
      </div>
      <div class="character-card"></div>
      
      <div class="upload-card">
        <div class="upload-header">
          <h4 class="upload-title">智能导入简历</h4>
          <i class="el-icon-document"></i>
        </div>
        <div class="upload-buttons">
          <button 
            @click="triggerFileUpload" 
            :disabled="isParsing"
            class="upload-button"
          >
            <i v-if="isParsing" class="el-icon-loading"></i>
            <i v-else class="el-icon-upload2"></i>
            <span>{{ isParsing ? '解析中...' : '上传简历文件' }}</span>
          </button>
          <button 
            @click="openWechatUploadModal" 
            class="upload-button wechat-upload-button"
          >
            <i class="el-icon-mobile-phone"></i>
            <span>微信扫码上传</span>
          </button>
        </div>
        <input 
          ref="fileInput"
          type="file" 
          class="file-input-hidden"
          accept=".pdf,.doc,.docx"
          @change="handleFileUpload"
        />
        <div v-if="fileName" class="file-name">{{ fileName }}</div>
      </div>
      
      <!-- 微信扫码上传弹窗 -->
      <div class="wechat-modal-container" v-if="showWechatModal" @click.self="closeWechatModal">
        <div class="wechat-modal-mask"></div>
        <div class="wechat-modal-content" @click.stop>
          <div class="wechat-header">
            <div class="wechat-title">扫码上传简历</div>
            <div class="wechat-close-btn" @click.stop="closeWechatModal">
              <i class="el-icon-close"></i>
            </div>
          </div>
          <div class="wechat-content">
            <div class="wechat-qr-container">
              <div v-if="qrCodeLoading" class="qr-loading">
                <div class="spinner"></div>
                <p>正在生成二维码...</p>
              </div>
              <div v-else>
                <img class="wechat-qr-code" :src="wechatQRCodeUrl" alt="微信扫码二维码" />
                <p class="wechat-hint">使用微信扫一扫上传简历</p>
                <p class="wechat-hint-sub">支持PDF格式文件</p>
                <div class="connection-status">
                  <span class="status-dot" :class="{ connected: wsConnected }"></span>
                  <span class="status-text">{{ wsConnected ? '已连接' : '等待连接...' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="guide-right">
      <div class="step-card">
        <div class="step-header">
          <div class="step-title-wrapper">
            <h2 class="step-title">{{ currentStepMetadata.title }} <span class="step-number">{{ currentStep }}/6</span></h2>
            <span class="step-badge">{{ currentStepMetadata.title }}</span>
          </div>
          <div class="progress-bar">
            <div 
              class="progress-fill" 
              :style="{ width: `${(currentStep / 6) * 100}%` }"
            ></div>
          </div>
          <p class="step-subtitle">{{ currentStepMetadata.subTitle }}</p>
        </div>

        <div class="step-content-wrapper">
          <!-- 第一步：基础信息 -->
          <el-form 
            v-show="currentStep === 1" 
            ref="step1Form"
            :model="formData" 
            :rules="step1Rules"
            label-position="top"
            class="step-content"
          >
            <el-form-item label="姓名" prop="real_name" class="form-section">
              <el-input 
                v-model="formData.real_name"
                placeholder="请输入您的姓名"
                class="form-input"
                maxlength="20"
                show-word-limit
              />
            </el-form-item>

            <el-form-item label="求职类型" prop="job_type" class="form-section">
              <div class="recruitment-type-grid">
                <button
                  v-for="type in jobTypes"
                  :key="type.value"
                  @click="handleRecruitmentTypeChange(type.value)"
                  type="button"
                  class="type-button"
                  :class="{ active: formData.job_type === type.value }"
                >
                  <i :class="getJobTypeIcon(type.value)"></i>
                  <span>{{ type.label }}</span>
                </button>
              </div>
            </el-form-item>

            <el-form-item label="求职状态" prop="work_status" class="form-section">
              <div class="work-status-grid">
                <button
                  v-for="status in workStatuses"
                  :key="status.value"
                  @click="formData.work_status = status.value"
                  type="button"
                  class="status-button"
                  :class="{ active: formData.work_status === status.value }"
                >
                  {{ status.label }}
                </button>
              </div>
            </el-form-item>
          </el-form>

          <!-- 第二步：求职期望 -->
          <el-form 
            v-show="currentStep === 2" 
            ref="step2Form"
            :model="formData" 
            :rules="step2Rules"
            label-position="top"
            class="step-content step-content-no-scroll"
          >
            <el-row :gutter="24">
              <el-col :span="24">
                <el-form-item label="期望职位" prop="job_objective">
                  <el-input 
                    v-model="formData.job_objective"
                    placeholder="请输入期望职位"
                    class="form-input"
                  />
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="24">
              <el-col :span="24">
                <el-form-item label="期望城市" prop="desired_city">
                  <el-select
                    v-model="formData.desired_city"
                    multiple
                    filterable
                    collapse-tags
                    clearable
                    placeholder="搜索心动城市"
                    class="form-input city-select"
                  >
                    <el-option label="全国" value="全国" />
                    <el-option-group label="热门城市">
                      <el-option v-for="c in hotCities" :key="c" :label="c" :value="c" />
                    </el-option-group>
                    <el-option-group v-for="(list, letter) in citiesByLetter" :key="letter" :label="letter">
                      <el-option v-for="c in list" :key="c" :label="c" :value="c" />
                    </el-option-group>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item label="期望薪资" prop="expected_salary_start">
                  <div class="salary-input-row">
                    <el-input 
                      v-model="formData.expected_salary_start" 
                      type="text" 
                      :placeholder="isIntern ? '最低薪资(元/天)' : '最低薪资(K/月)'"
                      class="salary-input"
                    />
                    <span class="salary-dash">—</span>
                    <el-input 
                      v-model="formData.expected_salary_end" 
                      type="text"  
                      :placeholder="isIntern ? '最高薪资(元/天)' : '最高薪资(K/月)'"
                      class="salary-input"
                    />
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>

          <!-- 第三步：教育经历 -->
          <el-form 
            v-show="currentStep === 3" 
            ref="step3Form"
            :model="formData" 
            :rules="step3Rules"
            label-position="top"
            class="step-content step-content-with-list"
          >
            <div class="resume-list-container">
              <div class="resume-list">
              <div 
                v-for="(edu, idx) in formData.education_list" 
                :key="idx" 
                class="resume-item"
              >
                <div class="resume-item-header">
                  <span class="resume-item-index">教育经历 {{ idx + 1 }}</span>
                  <div 
                    v-if="formData.education_list.length > 1"
                    class="resume-item-delete" 
                    @click="removeEducation(idx)"
                  >
                    <i class="el-icon-delete"></i>
                    <span>删除</span>
                  </div>
                </div>
                <div>
                  <el-row :gutter="24">
                    <el-col :xs="24" :sm="12">
                      <el-form-item label="学校名称" :prop="`education_list.${idx}.school`" :rules="educationRules.school">
                        <el-input 
                          v-model="edu.school" 
                          placeholder="学校名称" 
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="12">
                      <el-form-item label="专业" :prop="`education_list.${idx}.major`" :rules="educationRules.major">
                        <el-input 
                          v-model="edu.major" 
                          placeholder="专业名称" 
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="12">
                      <el-form-item label="学历" :prop="`education_list.${idx}.degree`" :rules="educationRules.degree">
                        <el-input 
                          v-model="edu.degree" 
                          placeholder="例如：本科" 
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="12">
                      <el-form-item label="GPA" :prop="`education_list.${idx}.gpa`">
                        <el-input 
                          v-model="edu.gpa" 
                          placeholder="请输入GPA"
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="24">
                      <el-form-item label="时间段" :prop="`education_list.${idx}.start_year`" :rules="educationRules.start_year">
                        <el-date-picker
                          :value="formatDateRangeForPicker(edu.start_year, edu.end_year)"
                          @input="handleDateRangeChange($event, edu, 'start_year', 'end_year')"
                          type="daterange"
                          range-separator="至"
                          start-placeholder="开始时间"
                          end-placeholder="结束时间"
                          value-format="yyyy-MM-dd"
                          style="width: 100%"
                        />
                      </el-form-item>
                    </el-col>
                  </el-row>
                </div>
              </div>
              </div>
              <button 
                @click="addEducation"
                type="button"
                class="add-item-btn"
              >
                <i class="el-icon-plus"></i>
                添加教育经历
              </button>
            </div>
          </el-form>

          <!-- 第四步：工作经历 -->
          <el-form 
            v-show="currentStep === 4" 
            ref="step4Form"
            :model="formData" 
            :rules="step4Rules"
            label-position="top"
            class="step-content step-content-with-list"
          >
            <div class="resume-list-container">
              <div class="resume-list">
              <div 
                v-for="(exp, idx) in formData.work_experience_list" 
                :key="idx" 
                class="resume-item"
              >
                <div class="resume-item-header">
                  <span class="resume-item-index">工作经历 {{ idx + 1 }}</span>
                  <div 
                    class="resume-item-delete" 
                    @click="removeExperience(idx)"
                  >
                    <i class="el-icon-delete"></i>
                    <span>删除</span>
                  </div>
                </div>
                <div>
                  <el-row :gutter="24">
                    <el-col :xs="24" :sm="12">
                      <el-form-item label="公司名称" :prop="`work_experience_list.${idx}.company`">
                        <el-input 
                          v-model="exp.company" 
                          placeholder="公司名称" 
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="12">
                      <el-form-item label="职位" :prop="`work_experience_list.${idx}.role`" :rules="getExperienceRules(idx).role">
                        <el-input 
                          v-model="exp.role" 
                          placeholder="职位名称" 
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="12">
                      <el-form-item label="城市" :prop="`work_experience_list.${idx}.city`">
                        <el-input 
                          v-model="exp.city" 
                          placeholder="城市"
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="12">
                      <el-form-item label="国家" :prop="`work_experience_list.${idx}.country`">
                        <el-input 
                          v-model="exp.country" 
                          placeholder="国家"
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="24">
                      <el-form-item label="时间段" :prop="`work_experience_list.${idx}.start_year`" :rules="getExperienceRules(idx).start_year">
                        <el-date-picker
                          :value="formatDateRangeForPicker(exp.start_year, exp.end_year)"
                          @input="handleDateRangeChange($event, exp, 'start_year', 'end_year')"
                          type="daterange"
                          range-separator="至"
                          start-placeholder="开始时间"
                          end-placeholder="结束时间"
                          value-format="yyyy-MM-dd"
                          style="width: 100%"
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="24">
                      <el-form-item label="工作描述" :prop="`work_experience_list.${idx}.description`">
                        <el-input 
                          v-model="exp.description" 
                          type="textarea"
                          :rows="3"
                          placeholder="描述你的工作内容..."
                        />
                      </el-form-item>
                    </el-col>
                  </el-row>
                </div>
              </div>
              </div>
              <button 
                @click="addExperience"
                type="button"
                class="add-item-btn"
              >
                <i class="el-icon-plus"></i>
                添加工作经历
              </button>
            </div>
          </el-form>

          <!-- 第五步：项目经历 -->
          <el-form 
            v-show="currentStep === 5" 
            ref="step5Form"
            :model="formData" 
            :rules="step5Rules"
            label-position="top"
            class="step-content step-content-with-list"
          >
            <div class="resume-list-container">
              <div class="resume-list">
              <div 
                v-for="(proj, idx) in formData.project_list" 
                :key="idx" 
                class="resume-item"
              >
                <div class="resume-item-header">
                  <span class="resume-item-index">项目经历 {{ idx + 1 }}</span>
                  <div 
                    class="resume-item-delete" 
                    @click="removeProject(idx)"
                  >
                    <i class="el-icon-delete"></i>
                    <span>删除</span>
                  </div>
                </div>
                <div>
                  <el-row :gutter="24">
                    <el-col :xs="24" :sm="12">
                      <el-form-item label="项目名称" :prop="`project_list.${idx}.project_name`">
                        <el-input 
                          v-model="proj.project_name" 
                          placeholder="项目名称"
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="24">
                      <el-form-item label="项目时间" :prop="`project_list.${idx}.start_year`" :rules="getProjectRules(idx).start_year">
                        <el-date-picker
                          :value="formatDateRangeForPicker(proj.start_year, proj.end_year)"
                          @input="handleDateRangeChange($event, proj, 'start_year', 'end_year')"
                          type="daterange"
                          range-separator="至"
                          start-placeholder="开始时间"
                          end-placeholder="结束时间"
                          value-format="yyyy-MM-dd"
                          style="width: 100%"
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="24">
                      <el-form-item label="项目描述" :prop="`project_list.${idx}.description`">
                        <el-input 
                          v-model="proj.description" 
                          type="textarea"
                          :rows="3"
                          placeholder="描述项目内容..."
                        />
                      </el-form-item>
                    </el-col>
                  </el-row>
                </div>
              </div>
              </div>
              <button 
                @click="addProject"
                type="button"
                class="add-item-btn"
              >
                <i class="el-icon-plus"></i>
                添加项目经历
              </button>
            </div>
          </el-form>

          <!-- 第六步：奖项和自我评价 -->
          <el-form 
            v-show="currentStep === 6" 
            ref="step6Form"
            :model="formData" 
            :rules="step6Rules"
            label-position="top"
            class="step-content step-content-with-list"
          >
            <div class="resume-list-container">
              <div class="resume-list">
                <div 
                  v-for="(award, idx) in formData.award_list" 
                  :key="idx" 
                  class="resume-item"
                >
                  <div class="resume-item-header">
                    <span class="resume-item-index">奖项荣誉 {{ idx + 1 }}</span>
                    <div 
                      class="resume-item-delete" 
                      @click="removeAward(idx)"
                    >
                      <i class="el-icon-delete"></i>
                      <span>删除</span>
                    </div>
                  </div>
                  <div>
                    <el-row :gutter="24">
                      <el-col :xs="24" :sm="12">
                        <el-form-item label="奖项名称" :prop="`award_list.${idx}.title`">
                          <el-input 
                            v-model="award.title" 
                            placeholder="奖项名称" 
                          />
                        </el-form-item>
                      </el-col>
                      <el-col :xs="24" :sm="12">
                        <el-form-item label="获奖时间" :prop="`award_list.${idx}.date`">
                          <el-date-picker
                            v-model="award.date" 
                            type="date"
                            placeholder="选择日期"
                            value-format="yyyy-MM-dd"
                            style="width: 100%"
                          />
                        </el-form-item>
                      </el-col>
                      <el-col :xs="24" :sm="24">
                        <el-form-item label="描述" :prop="`award_list.${idx}.description`">
                          <el-input 
                            v-model="award.description" 
                            type="textarea"
                            :rows="2"
                            placeholder="奖项描述..."
                          />
                        </el-form-item>
                      </el-col>
                    </el-row>
                  </div>
                </div>
              </div>
              <button 
                @click="addAward"
                type="button"
                class="add-item-btn"
              >
                <i class="el-icon-plus"></i>
                添加奖项荣誉
              </button>
            </div>
            <el-form-item label="自我评价" prop="personal_profile" class="form-section">
              <template slot="label">
                <span class="form-label-with-icon">
                  <i class="el-icon-user"></i>
                  自我评价
                </span>
              </template>
              <el-input 
                type="textarea"
                v-model="formData.personal_profile"
                placeholder="简短描述你的个人优势、性格特点等..."
                :rows="5"
              />
            </el-form-item>
          </el-form>
        </div>

        <!-- 操作按钮 -->
        <div class="step-actions">
          <button 
            v-if="currentStep > 1"
            @click="prevStep"
            class="action-button action-button-back"
          >
            <i class="el-icon-arrow-left"></i>
            <span>上一步</span>
          </button>
          <div v-else></div>
          <button 
            @click="currentStep === 6 ? handleComplete() : nextStep()"
            class="action-button action-button-primary"
          >
            <span>{{ currentStep === 6 ? '核对并提交' : '下一步' }}</span>
            <i v-if="currentStep < 6" class="el-icon-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  uploadResume, 
  generate_qrcode,
  createWebSocketConnection,
  onWebSocketMessage,
  offWebSocketMessage,
  closeWebSocketConnection,
  parseResumeByUrl
} from '@/api/upload'
import { setInfo } from '@/api/user'
import { JOB_TYPES } from '@/constants/jobConstants'
import { HOT_CITIES, CITIES_BY_LETTER } from '@/constants/cityConstants'

const INITIAL_FORM_DATA = {
  real_name: '',
  job_type: '0',
  work_status: 1,
  job_objective: '',
  desired_city: [],
  expected_salary_start: '',
  expected_salary_end: '',
  education_list: [
    {
      school: '',
      major: '',
      degree: '',
      gpa: '',
      start_year: '',
      end_year: ''
    }
  ],
  work_experience_list: [
    {
      company: '',
      role: '',
      city: '',
      country: '',
      start_year: '',
      end_year: '',
      description: ''
    }
  ],
  project_list: [
    {
      project_name: '',
      start_year: '',
      end_year: '',
      description: ''
    }
  ],
  award_list: [
    {
      title: '',
      date: '',
      description: ''
    }
  ],
  personal_profile: ''
}

const STEP_METADATA = {
  1: { title: '基础信息', subTitle: '请填写您的姓名、求职类型和状态' },
  2: { title: '求职期望', subTitle: '告诉我们您的期望职位和薪资' },
  3: { title: '教育经历', subTitle: '填写您的教育背景' },
  4: { title: '工作经历', subTitle: '分享您的工作经验' },
  5: { title: '项目经历', subTitle: '展示您的项目经验' },
  6: { title: '奖项与评价', subTitle: '补充您的其他信息' }
}

export default {
  name: 'NewbieGuide',
  props: {
    visible: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentStep: 1,
      formData: JSON.parse(JSON.stringify(INITIAL_FORM_DATA)),
      isParsing: false,
      fileName: null,
      showWechatModal: false,
      wechatQRCodeUrl: '',
      qrCodeLoading: false,
      wsConnected: false,
      lastParseResumeTime: 0, // 上次解析时间，用于节流
      fileUploadCallback: null, // 保存文件上传回调，用于移除监听器
      connectionStatusCallback: null, // 保存连接状态回调，用于移除监听器
      workStatuses: [
        { value: 1, label: '离职-随时到岗' },
        { value: 2, label: '在职-月内到岗' },
        { value: 3, label: '在职-考虑机会' },
        { value: 4, label: '在职-暂不考虑' }
      ],
      jobTypes: JOB_TYPES,
      hotCities: HOT_CITIES,
      citiesByLetter: CITIES_BY_LETTER,
      // 表单验证规则
      step1Rules: {
        real_name: [
          { required: true, message: '请输入姓名', trigger: 'blur' },
          { min: 2, max: 20, message: '姓名长度在 2 到 20 个字符', trigger: 'blur' }
        ],
        job_type: [
          { required: true, message: '请选择求职类型', trigger: 'change' }
        ],
        work_status: [
          { required: true, message: '请选择求职状态', trigger: 'change' }
        ]
      },
      step2Rules: {
        job_objective: [
          { required: true, message: '请输入期望职位', trigger: 'blur' }
        ],
        desired_city: [
          { 
            type: 'array', 
            required: true, 
            message: '请选择工作地点', 
            trigger: 'change',
            min: 1
          }
        ],
        expected_salary_start: [
          { 
            required: true, 
            message: '请输入最低薪资', 
            trigger: 'blur',
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error('请输入最低薪资'))
              } else if (!this.formData.expected_salary_end) {
                callback(new Error('请输入最高薪资'))
              } else {
                callback()
              }
            }
          }
        ]
      },
      step3Rules: {},
      step4Rules: {},
      step5Rules: {},
      step6Rules: {},
      educationRules: {
        school: [
          { required: true, message: '请输入学校名称', trigger: 'blur' }
        ],
        major: [
          { required: true, message: '请输入专业', trigger: 'blur' }
        ],
        degree: [
          { required: true, message: '请输入学历', trigger: 'blur' }
        ],
        start_year: [
          { required: true, message: '请输入开始时间', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    currentStepMetadata() {
      return STEP_METADATA[this.currentStep] || STEP_METADATA[1]
    },
    isIntern() {
      return this.formData.job_type === '2' || this.formData.job_type === 2
    }
  },
  methods: {
    triggerFileUpload() {
      this.$refs.fileInput?.click()
    },
    
    handleRecruitmentTypeChange(value) {
      this.formData.job_type = value
    },
    
    getJobTypeIcon(value) {
      const iconMap = {
        '0': 'el-icon-suitcase',  // 社招
        '1': 'el-icon-school',    // 校招
        '2': 'el-icon-reading'   // 实习
      }
      return iconMap[value] || 'el-icon-suitcase'
    },
    
    async handleFileUpload(event) {
      const file = event.target.files?.[0]
      if (!file) return
      
      this.fileName = file.name
      this.isParsing = true
      
      try {
        const response = await uploadResume({
          file: file,
          url: '/api/upload/resume',
          encodeFileName: false
        })
        
        const responseData = response?.data?.data || response?.data || {}
        const parsedResume = responseData.parsed_resume
        
        if (parsedResume) {
          this.fillFormDataFromParsed(parsedResume)
          this.$message.success('简历上传成功，信息已自动填充')
        } else {
          this.$message.warning('简历上传成功，但未能解析内容，请手动填写')
        }
      } catch (error) {
        console.error('上传失败:', error)
        this.$message.error('简历上传失败，请重试')
      } finally {
        this.isParsing = false
      }
    },
    
    fillFormDataFromParsed(parsedResume) {
      Object.assign(this.formData, {
        real_name: parsedResume.real_name || parsedResume.name || parsedResume.full_name || '',
        job_type: parsedResume.job_type ? String(parsedResume.job_type) : '0',
        work_status: parsedResume.work_status || 1,
        job_objective: parsedResume.job_objective || '',
        desired_city: Array.isArray(parsedResume.desired_city) ? parsedResume.desired_city : (parsedResume.desired_city?.split(',') || []),
        expected_salary_start: parsedResume.expected_salary_start || '',
        expected_salary_end: parsedResume.expected_salary_end || '',
        education_list: parsedResume.education_list || [],
        work_experience_list: parsedResume.work_experience_list || [],
        project_list: parsedResume.project_list || [],
        award_list: parsedResume.award_list || [],
        personal_profile: parsedResume.personal_profile || ''
      })
      
      this.$message.success('简历解析成功，信息已自动填充到表单中')
    },
    
    addEducation() {
      this.formData.education_list.push({
        school: '',
        major: '',
        degree: '',
        gpa: '',
        start_year: '',
        end_year: ''
      })
    },
    
    removeEducation(index) {
      if (this.formData.education_list.length > 1) {
        this.formData.education_list.splice(index, 1)
      }
    },
    
    addExperience() {
      this.formData.work_experience_list.push({
        company: '',
        role: '',
        city: '',
        country: '',
        start_year: '',
        end_year: '',
        description: ''
      })
    },
    
    removeExperience(index) {
      this.formData.work_experience_list.splice(index, 1)
    },
    
    addProject() {
      this.formData.project_list.push({
        project_name: '',
        start_year: '',
        end_year: '',
        description: ''
      })
    },
    
    removeProject(index) {
      this.formData.project_list.splice(index, 1)
    },
    
    addAward() {
      this.formData.award_list.push({
        title: '',
        date: '',
        description: ''
      })
    },
    
    removeAward(index) {
      this.formData.award_list.splice(index, 1)
    },
    
    async nextStep() {
      const form = this.$refs[`step${this.currentStep}Form`]
      if (form) {
        try {
          await form.validate()
        } catch (error) {
          return
        }
      }
      
      if (this.currentStep === 2 && !this.formData.expected_salary_end) {
        this.$message.warning('请输入最高薪资')
        return
      }
      
      if (this.currentStep < 6) {
        this.currentStep++
        this.$nextTick(() => {
          const wrapper = document.querySelector('.step-content-wrapper')
          if (wrapper) wrapper.scrollTop = 0
        })
      }
    },
    
    prevStep() {
      this.currentStep--
    },
    
    async handleComplete() {
      const form = this.$refs.step6Form
      if (form) {
        try {
          await form.validate()
        } catch (error) {
          this.$message.warning('请完善必填信息')
          return
        }
      }
      
      try {
        const submitData = {
          ...this.formData,
          desired_city: Array.isArray(this.formData.desired_city)
            ? this.formData.desired_city.join(',')
            : (this.formData.desired_city || ''),
          education_list: JSON.stringify(this.formData.education_list || []),
          work_experience_list: JSON.stringify(this.formData.work_experience_list || []),
          project_list: JSON.stringify(this.formData.project_list || []),
          award_list: JSON.stringify(this.formData.award_list || [])
        }
        
        // 调用 setInfo 接口
        const res = await setInfo(submitData)
        if (res && res.data && res.data.code === 1) {
          this.$message.success('信息填写完成！')
          this.$emit('complete', submitData)
        } else {
          this.$message.error(res?.data?.msg || '保存失败，请稍后重试')
        }
      } catch (error) {
        console.error('保存个人信息失败:', error)
        this.$message.error('网络错误，请检查网络连接后重试')
      }
    },
    
    formatDateRangeForPicker(startValue, endValue) {
      if (!startValue || !endValue) return null
      return [
        /^\d{4}$/.test(startValue) ? `${startValue}-01-01` : startValue,
        /^\d{4}$/.test(endValue) ? `${endValue}-12-31` : endValue
      ]
    },
    
    handleDateRangeChange(value, item, startField, endField) {
      if (value?.length === 2) {
        item[startField] = value[0] || ''
        item[endField] = value[1] || ''
      } else {
        item[startField] = ''
        item[endField] = ''
      }
    },
    
    getExperienceRules(idx) {
      return {
        company: [],
        role: [
          { 
            validator: (rule, value, callback) => {
              const exp = this.formData.work_experience_list[idx]
              if (exp.company?.trim() && !value?.trim()) {
                callback(new Error('请输入职位'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        start_year: [
          { 
            validator: (rule, value, callback) => {
              const exp = this.formData.work_experience_list[idx]
              if (exp.company?.trim() && (!value || !exp.end_year)) {
                callback(new Error('请选择时间段'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      }
    },
    
    getProjectRules(idx) {
      return {
        project_name: [],
        start_year: [
          { 
            validator: (rule, value, callback) => {
              const proj = this.formData.project_list[idx]
              if (proj.project_name?.trim() && (!value || !proj.end_year)) {
                callback(new Error('请选择项目时间'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      }
    },
    
    // 微信扫码上传相关方法
    openWechatUploadModal() {
      this.showWechatModal = true
      this.generateWechatQRCode()
    },
    
    closeWechatModal() {
      this.showWechatModal = false
      // 移除WebSocket监听器
      if (this.fileUploadCallback) {
        offWebSocketMessage("file_upload_complete", this.fileUploadCallback)
        this.fileUploadCallback = null
      }
      if (this.connectionStatusCallback) {
        offWebSocketMessage("connection_status", this.connectionStatusCallback)
        this.connectionStatusCallback = null
      }
      if (this.wsConnected) {
        closeWebSocketConnection()
        this.wsConnected = false
      }
    },
    
    async generateWechatQRCode() {
      try {
        this.qrCodeLoading = true
        
        const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}")
        const userId = userInfo.id || userInfo.userId
        
        if (!userId) {
          this.$message.error("请先登录")
          this.closeWechatModal()
          return
        }
        
        const response = await generate_qrcode()
        
        if (response.data && response.data.code === 1) {
          this.wechatQRCodeUrl = `data:image/png;base64,${response.data.data}`
          await this.connectWebSocket(userId)
          this.$message.success("二维码生成成功，请使用微信扫码上传")
        } else {
          this.$message.error(response.data?.msg || "二维码生成失败")
        }
      } catch (error) {
        console.error("生成二维码失败:", error)
        this.$message.error("二维码生成失败，请稍后再试")
      } finally {
        this.qrCodeLoading = false
      }
    },
    
    async connectWebSocket(userId) {
      try {
        await createWebSocketConnection(userId)
        this.wsConnected = true
        console.log("WebSocket连接成功")
        this.setupWebSocketListeners()
      } catch (error) {
        console.error("WebSocket连接失败:", error)
        this.$message.warning("实时连接失败，但二维码仍可使用")
      }
    },
    
    setupWebSocketListeners() {
      // 先移除旧的监听器，避免重复注册
      if (this.fileUploadCallback) {
        offWebSocketMessage("file_upload_complete", this.fileUploadCallback)
      }
      if (this.connectionStatusCallback) {
        offWebSocketMessage("connection_status", this.connectionStatusCallback)
      }
      
      // 创建新的回调函数并保存
      this.fileUploadCallback = (data) => {
        console.log("收到文件上传完成消息:", data)
        this.handleWechatFileUploadComplete(data)
      }
      
      this.connectionStatusCallback = (data) => {
        console.log("WebSocket连接状态:", data)
        this.wsConnected = data.connected
      }
      
      // 注册新的监听器
      onWebSocketMessage("file_upload_complete", this.fileUploadCallback)
      onWebSocketMessage("connection_status", this.connectionStatusCallback)
    },
    
    // 节流包装的 parseResumeByUrl 调用
    async throttledParseResumeByUrl(resumeUrl) {
      const now = Date.now()
      const throttleDelay = 3000 // 2秒节流间隔
      
      // 如果距离上次调用不足2秒，则忽略本次调用
      if (now - this.lastParseResumeTime < throttleDelay) {
        return null
      }
      
      this.lastParseResumeTime = now
      
      try {
        const response = await parseResumeByUrl(resumeUrl)
        return response
      } catch (error) {
        console.error('调用解析接口失败:', error)
        throw error
      }
    },
    
    async handleWechatFileUploadComplete(data) {
      console.log("收到文件上传完成消息:", data)
      const resumeUrl = data.resume_url || data.resumeUrl
      const resumeName = data.resume_name || data.resumeName || "微信上传简历.pdf"
      
      if (!resumeUrl) {
        this.$message.warning('未收到简历文件')
        return
      }
      
      this.fileName = resumeName
      this.isParsing = true
      
      try {
        const response = await this.throttledParseResumeByUrl(resumeUrl)
        
        // 如果被节流忽略，直接返回
        if (!response) {
          this.isParsing = false
          this.closeWechatModal()
          return
        }
        
        if (response && response.data && response.data.code === 1) {
          const parsedResume = response.data.data?.parsed_resume
          
          if (parsedResume) {
            this.fillFormDataFromParsed(parsedResume)
            this.$message.success('简历上传成功，信息已自动填充')
          }
        } else {
          console.error('解析简历失败:', response)
          this.$message.warning('简历解析失败，请手动填写')
        }
      } catch (error) {
        console.error('调用解析接口失败:', error)
        this.$message.error('解析简历失败，请稍后重试')
      } finally {
        this.isParsing = false
        this.closeWechatModal()
      }
    }
  },
  
  beforeDestroy() {
    // 移除WebSocket监听器
    if (this.fileUploadCallback) {
      offWebSocketMessage("file_upload_complete", this.fileUploadCallback)
    }
    if (this.connectionStatusCallback) {
      offWebSocketMessage("connection_status", this.connectionStatusCallback)
    }
    if (this.wsConnected) {
      closeWebSocketConnection()
      this.wsConnected = false
    }
  }
}
</script>

<style scoped lang="scss">
.newbie-guide-container {
  width: 100%;
  height: 100%;
  background: #f0f4f7;
  display: flex;
  max-width: 1400px;
  margin: 0 auto;
  box-sizing: border-box;
  overflow: hidden;
}

.guide-left {
  width: 40%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.guide-title {
  padding: 24px 24px 16px 24px;
  text-align: center;
  
  .title-main {
    font-size: 24px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 8px 0;
  }
  
  .title-subtitle {
    font-size: 14px;
    color: #909399;
    margin: 0;
  }
}

.character-card {
  width: 100%;
  height: 300px;
  background-image: url('../assets/img/Cathy.png');
  background-size: contain;
  background-position: center top;
  background-repeat: no-repeat;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60%;
    background: linear-gradient(to bottom, transparent 0%, rgba(240, 244, 247, 0.3) 30%, rgba(240, 244, 247, 0.8) 70%, rgba(240, 244, 247, 1) 100%);
    pointer-events: none;
  }
}

.upload-card {
  background: rgba(255, 255, 255, 0.9);
  margin: 24px;
  border-radius: 24px;
  padding: 4px 24px 24px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.upload-title {
  font-size: 14px;
  font-weight: 900;
  color: #1e293b;
}

.upload-header i {
  font-size: 20px;
  color: #667eea;
}

.upload-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.upload-button {
  width: 100%;
  height: 56px;
  border: 2px dashed #c7d2fe;
  border-radius: 16px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #667eea;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: rgba(99, 102, 241, 0.05);
    border-color: #667eea;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  i {
    font-size: 20px;
  }
  
  &.wechat-upload-button {
    border-color: #07c160;
    color: #07c160;
    
    &:hover:not(:disabled) {
      background: rgba(7, 193, 96, 0.05);
      border-color: #07c160;
    }
  }
}

.file-input-hidden {
  display: none;
}

.file-name {
  margin-top: 12px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
}

.guide-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  
  ::v-deep {
    .el-form-item__label {
      padding: 0;
    }
  }
}

.step-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  max-height: 100%;
}

.step-header {
  margin-bottom: 12px;
  flex-shrink: 0;
}

.step-title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 12px;
  gap: 12px;
}

.step-title {
  font-size: 20px;
  font-weight: 900;
  color: #1e293b;
  line-height: 1.2;
  
  .step-number {
    color: #cbd5e1;
    font-weight: 500;
    margin-left: 8px;
  }
}

.step-badge {
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  color: #667eea;
  background: #eef2ff;
  padding: 4px 12px;
  border-radius: 999px;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 12px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(to right, #62AFFF, #9C41FF);
  transition: width 0.5s ease-out;
  border-radius: 999px;
}

.step-subtitle {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 700;
  margin-top: 12px;
}

.step-content-wrapper {
  flex: 1 1 0;
  overflow: hidden;
  padding-right: 12px;
  margin-bottom: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.step-content {
  animation: fadeIn 0.5s ease-out;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

.step-content-with-list {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  flex: 1 1 0;
}

.step-content-no-scroll {
  overflow: visible;
  flex: 1;
  display: flex;
  flex-direction: column;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-section {
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.form-label-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 900;
  color: #1e293b;
  margin-bottom: 16px;
  
  i {
    font-size: 16px;
    
    &.el-icon-trophy {
      color: #f59e0b;
    }
    
    &.el-icon-user {
      color: #3b82f6;
    }
  }
}

.recruitment-type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.type-button {
  height: 96px;
  border-radius: 24px;
  border: 2px solid #f1f5f9;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.2s;
  box-sizing: border-box;
  
  i {
    font-size: 24px;
  }
  
  span {
    font-size: 12px;
    font-weight: 700;
  }
  
  &:hover {
    border-color: #e2e8f0;
  }
  
  &.active {
    border-color: #818cf8;
    background: rgba(99, 102, 241, 0.05);
    color: #4f46e5;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.work-status-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.status-button {
  font-size: 12px;
  font-weight: 700;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  background: white;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  box-sizing: border-box;
  
  &:hover {
    background: #f8fafc;
  }
  
  &.active {
    background: #6366f1;
    color: white;
    border-color: #6366f1;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
  }
}

.salary-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .salary-input {
    flex: 1;
    min-width: 0;
  }
  
  .salary-dash {
    color: #606266;
    font-weight: 500;
    flex-shrink: 0;
  }
}

.city-select {
  width: 100%;
}

.resume-list-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

.resume-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
    
    &:hover {
      background: #94a3b8;
    }
  }
}

.resume-item {
  position: relative;
  margin-bottom: 0;
}

.resume-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.resume-item-index {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
}

.resume-item-delete {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 6px;
  font-size: 13px;
  
  &:hover {
    color: #ef4444;
    background: #fee2e2;
  }
  
  i {
    font-size: 14px;
  }
  
  span {
    font-size: 13px;
  }
}

.add-item-btn {
  flex-shrink: 0;
  margin-top: 16px;
  width: 100%;
  padding: 8px;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.05);
  }
  
  i {
    font-size: 16px;
  }
}

.step-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding-top: 16px;
  padding-bottom: 0;
  border-top: 1px solid rgba(241, 245, 249, 0.5);
  flex-shrink: 0;
  margin-top: auto;
  position: relative;
  z-index: 10;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  padding: 0 24px;
  border-radius: 12px;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  box-sizing: border-box;
  
  i {
    font-size: 20px;
  }
}

.action-button-back {
  background: white;
  border: 1px solid #f1f5f9;
  color: #94a3b8;
  min-width: 120px;
  
  &:hover {
    background: #f8fafc;
    border-color: #e2e8f0;
  }
}

.action-button-primary {
  flex: 1;
  background: linear-gradient(135deg, #62AFFF 0%, #9C41FF 100%);
  color: white;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.3);
  min-width: 200px;
  
  &:hover {
    transform: scale(1.01);
    box-shadow: 0 6px 24px rgba(99, 102, 241, 0.4);
  }
  
  &:active {
    transform: scale(0.98);
  }
}

// 响应式设计
@media (max-width: 1024px) {
  .newbie-guide-container {
    flex-direction: column;
    padding: 16px;
  }
  
  .guide-left {
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
  }
  
  .step-card {
    border-radius: 24px;
    padding: 24px;
  }
}

@media (max-width: 640px) {
  .recruitment-type-grid {
    grid-template-columns: 1fr;
  }
  
  .work-status-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

// 匹配 ResumeInfo 的样式
::v-deep .resume-item {
  .el-form-item {
    margin-bottom: 8px;
  }
  
  .el-form-item__label {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    padding-bottom: 0;
  }
}

// 微信扫码上传弹窗样式
.wechat-modal-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wechat-modal-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.wechat-modal-content {
  position: relative;
  background: white;
  border-radius: 24px;
  padding: 0;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  z-index: 1;
}

.wechat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #f1f5f9;
}

.wechat-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

.wechat-close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #94a3b8;
  
  &:hover {
    background: #f1f5f9;
    color: #64748b;
  }
  
  i {
    font-size: 20px;
  }
}

.wechat-content {
  padding: 24px;
}

.wechat-qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.qr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f1f5f9;
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  p {
    color: #64748b;
    font-size: 14px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.wechat-qr-code {
  width: 240px;
  height: 240px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  background: white;
}

.wechat-hint {
  font-size: 14px;
  color: #64748b;
  text-align: center;
  margin: 0;
}

.wechat-hint-sub {
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  margin: 0;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #cbd5e1;
    transition: background 0.3s;
    
    &.connected {
      background: #10b981;
      box-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
    }
  }
  
  .status-text {
    font-size: 12px;
    color: #64748b;
  }
}
</style>

